import type { Dispatch, SetStateAction } from "react";
import toast from "react-hot-toast";
import { useDevAgentStore } from "@/stores/devAgentStore";
import type { MetadataBranch } from "@/services/metadata_v1/types";

const MAX_ATTEMPTS = 5;
const MAX_STUCK_BEFORE_BAIL = 3;

export type SaveErrorState = { message: string; code: string } | null;

export async function runSessionSaveWithValidation(params: {
  sessionUuid: string;
  activeFileId: string;
  className: string;
  metadataType: string;
  content: string;
  /** Existing branch for this file, if any. When undefined, `saveBranchAsync`
      creates a new branch; when defined, it updates the existing one. */
  activeBranch?: MetadataBranch | null;
  /** Async form of the `useSaveBranchFile` mutation — persists validated code
      as a branch so it shows up in the deployment tab. */
  saveBranchAsync: (input: {
    metadataItemId: string;
    content: string;
    name?: string;
  }) => Promise<MetadataBranch>;
  /** When provided (i.e. this file is virtual — not yet registered in
      ncoder-backend), used to create the metadata_item row before the branch
      save. Required fields: orgId + retrievalId so the new item is linked to
      the correct retrieval. */
  ensureMetadataItemId?: () => Promise<string | null>;
  setIsSaving: Dispatch<SetStateAction<boolean>>;
  setSaveErrors: Dispatch<SetStateAction<SaveErrorState>>;
  setContentByFileId: Dispatch<SetStateAction<Record<string, string>>>;
  setDirtyByFileId: Dispatch<SetStateAction<Record<string, boolean>>>;
  setOriginalByFileId: Dispatch<SetStateAction<Record<string, string>>>;
  onFileSaved?: (data: {
    id: string;
    fileName: string;
    metadataType: string;
  }) => void;
  /** When branch save used a canonical metadata UUID different from the tab id (virtual → real). */
  onTabIdResolved?: (canonicalId: string, previousTabId: string) => void;
}): Promise<void> {
  const {
    sessionUuid,
    activeFileId,
    className,
    metadataType,
    content,
    activeBranch,
    saveBranchAsync,
    ensureMetadataItemId,
    setIsSaving,
    setSaveErrors,
    setContentByFileId,
    setDirtyByFileId,
    setOriginalByFileId,
    onFileSaved,
    onTabIdResolved,
  } = params;
  // Must use getState() — this module is a plain async function, not a component;
  // useDevAgentStore(selector) is a hook and would throw React #321.
  const setArtifactBadge = useDevAgentStore.getState().setArtifactBadge;

  setIsSaving(true);
  setSaveErrors(null);
  const { validateAndSave, fixCode } = await import("../../devAgentService");
  let currentCode = content;
  let lastErrMsg = "";
  let stuckRuns = 0;

  try {
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      setSaveErrors({
        message:
          attempt === 1
            ? "Validating..."
            : `Re-validating (attempt ${attempt}/${MAX_ATTEMPTS})...`,
        code: currentCode,
      });
      const result = await validateAndSave(sessionUuid, {
        class_name: className,
        content: currentCode,
        metadata_type: metadataType,
      });

      if (result.valid) {
        // Validation passed — persist as a branch so it shows up in the
        // deployment tab. For virtual files (AI-generated, not yet in
        // ncoder-backend) we need to create the metadata_item first via
        // `ensureMetadataItemId`, then branch-save against that new id.
        let persistKey = activeFileId;
        try {
          // If activeFileId is a real UUID → update/create branch on it.
          // Otherwise it's a virtual/chat-generated file → register it via
          // createMetadataItem (through `ensureMetadataItemId`), then save.
          const uuidRe =
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          const itemId = uuidRe.test(activeFileId)
            ? activeFileId
            : await ensureMetadataItemId?.();
          if (!itemId) {
            throw new Error(
              "This file isn't registered yet and no create path is available.",
            );
          }
          await saveBranchAsync({
            metadataItemId: itemId,
            content: currentCode,
            name: activeBranch ? undefined : `edit-${className}`,
          });
          persistKey = itemId;
        } catch (branchErr: unknown) {
          const branchMsg =
            branchErr && typeof branchErr === "object" && "message" in branchErr
              ? String((branchErr as Error).message)
              : "Failed to save branch";
          setSaveErrors({
            message: `Code is valid but the branch save failed: ${branchMsg}`,
            code: currentCode,
          });
          toast.error("Validation passed — branch save failed");
          return;
        }
        const prevKey = activeFileId;
        setContentByFileId((prev) => {
          const next = { ...prev };
          if (persistKey !== prevKey) delete next[prevKey];
          next[persistKey] = currentCode;
          return next;
        });
        setDirtyByFileId((prev) => {
          const next = { ...prev };
          if (persistKey !== prevKey) delete next[prevKey];
          next[persistKey] = false;
          return next;
        });
        setOriginalByFileId((prev) => {
          const next = { ...prev };
          if (persistKey !== prevKey) delete next[prevKey];
          next[persistKey] = currentCode;
          return next;
        });
        if (persistKey !== prevKey) {
          onTabIdResolved?.(persistKey, prevKey);
          const st = useDevAgentStore.getState();
          const prevBadge = st.artifactBadges[prevKey];
          if (prevBadge) st.setArtifactBadge(persistKey, prevBadge);
        }
        // Drop virtual-file entries for this tab — persisting to a branch means
        // the item is real; keeping vf[id] would block active-branch UI if any
        // code path still keyed off store virtual state.
        useDevAgentStore.setState((s) => {
          const vf = { ...s.virtualFiles };
          delete vf[prevKey];
          delete vf[persistKey];
          return { virtualFiles: vf };
        });
        setArtifactBadge(persistKey, "M");
        setSaveErrors(null);
        toast.success(
          attempt === 1
            ? "Validated and saved"
            : `Fixed and saved (attempt ${attempt})`,
        );
        onFileSaved?.({
          id: persistKey,
          fileName: className,
          metadataType,
        });
        return;
      }

      const errors = result.errors || [];
      if (errors.length === 0) {
        setSaveErrors({
          message:
            "Validation failed but the server did not return any errors. Try saving again or check the backend logs.",
          code: currentCode,
        });
        toast.error("Validation failed (no error details)");
        return;
      }

      const errMsg = errors
        .map((e: { message?: string; line?: number }) => {
          const line = e.line ? ` (line ${e.line})` : "";
          const msg = typeof e === "string" ? e : (e.message || "Compilation error (no details)");
          return `${msg}${line}`;
        })
        .join("; ");
      lastErrMsg = errMsg;

      if (attempt >= MAX_ATTEMPTS) {
        // Open dependent classes if the fix agent identified them
        const depClasses: string[] = result.dependent_classes || [];
        if (depClasses.length > 0) {
          const store = useDevAgentStore.getState();
          for (const cls of depClasses) {
            try {
              const { getMetadataItemByName } = await import("../../devAgentService");
              const orgId = (await import("@/stores/authStore_v1")).useAuthStoreV1.getState().selectedOrg?.id || "";
              const item = await getMetadataItemByName(cls, orgId);
              if (item?.id) {
                store.setPendingFile({ id: item.id, name: cls, metadataType: "ApexClass", content: item.content || "" });
                store.setEditorOpen(true);
              }
            } catch { /* class not in DB — skip */ }
          }
          setSaveErrors({
            message: `Failed after ${MAX_ATTEMPTS} attempts: ${errMsg}\n\nDependent classes opened: ${depClasses.join(", ")}`,
            code: currentCode,
          });
          toast(`Opened ${depClasses.join(", ")} — fix those first`, { icon: "ℹ️", duration: 5000 });
        } else {
          setSaveErrors({
            message: `Failed after ${MAX_ATTEMPTS} attempts: ${errMsg}`,
            code: currentCode,
          });
          toast.error("Could not fix compilation errors");
        }
        return;
      }

      setSaveErrors({
        message: `Fixing (attempt ${attempt}/${MAX_ATTEMPTS}): ${errMsg}`,
        code: currentCode,
      });
      try {
        const fix = await fixCode(sessionUuid, {
          class_name: className,
          content: currentCode,
          errors,
          metadata_type: metadataType,
        });
        if (fix.fixed && fix.content && fix.content !== currentCode) {
          // Hold the proposed fix in memory only — don't overwrite the
          // editor mid-loop. If validation eventually passes we commit the
          // fixed code to the editor; if the loop bails we leave the user's
          // original content intact so they can edit manually. The proposed
          // fix is still shown to the user via `setSaveErrors.code` so they
          // can see what the LLM tried.
          currentCode = fix.content;
          stuckRuns = 0;
        } else {
          stuckRuns += 1;
          if (stuckRuns >= MAX_STUCK_BEFORE_BAIL) {
            setSaveErrors({
              message: `Auto-fix is stuck after ${stuckRuns} attempts producing no change. Last error: ${errMsg}`,
              code: currentCode,
            });
            toast.error("Auto-fix is stuck — manual edit required");
            return;
          }
        }
      } catch (fixErr: unknown) {
        stuckRuns += 1;
        const fixMsg =
          fixErr && typeof fixErr === "object" && "message" in fixErr
            ? String((fixErr as Error).message)
            : "";
        if (stuckRuns >= MAX_STUCK_BEFORE_BAIL) {
          setSaveErrors({
            message: `Auto-fix request failed ${stuckRuns} times in a row: ${fixMsg || errMsg}`,
            code: currentCode,
          });
          toast.error("Auto-fix request failed");
          return;
        }
        setSaveErrors({
          message: `Fix attempt ${attempt} failed (${fixMsg || "network"}), retrying…`,
          code: currentCode,
        });
      }
    }
  } catch (err: unknown) {
    const msg =
      err && typeof err === "object" && "message" in err
        ? String((err as Error).message)
        : "Save failed";
    toast.error(msg);
    setSaveErrors({
      message: msg || lastErrMsg || "Save failed",
      code: currentCode,
    });
  } finally {
    setIsSaving(false);
  }
}
